import React, { useState, useEffect, useMemo } from 'react';

import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage, useForm } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { APPCONFIG } from '../../app.config';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { useManagedContext } from '../../components/hooks';
import { Loading, TableCustom, SectionHeading, AdvancedSearch, InputWithEnterEvent, Select } from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import UsuarioModal from '../../components/controls/UsuarioModal';
import { useAccess } from '../../components/hooks/useAccess';
import { useLista } from '../../components/hooks/useLista';
import { useLocation } from 'react-router-dom';
import { TIPS } from '../../consts/tips';


function UsuariosView() {
    const { showMessageModal } = useManagedContext()
    const location = useLocation()

    //hooks
    const [state, setState] = useState({
        loading: false,
        showForm: false,
        modeFormEdit: false,
        rowId: 0,
        list: []
    });
    const [dataTaggerId, setDataTaggerId] = useState(null)

    const [filters, setFilters] = useState({
        labels: [
            { title: 'Código', field: 'codigo', value: '', valueIgnore: ''},
            { title: 'Nombre y apellido', field: 'nombreApellido', value: '', valueIgnore: '' },
            { title: 'Email', field: 'email', value: '', valueIgnore: '' },
            { title: 'Estado', field: 'estadoUsuarioDesc', fieldOriginal: 'idEstadoUsuario', value: 'Activo', valueIgnore: '', valueIgnoreOriginal: '' }
        ]
    });

    const [ formValues, formHandle, , formSet ] = useForm({
        codigo: '',
        nombreApellido: '',
        email: '',
        idEstadoUsuario: 10,
        estadoUsuarioDesc: 'Activo'
    });

    const handleEstadoChange = (event) => {
        const idEstadoUsuario = event.target.value;
        const estadoUsuarioDesc = idEstadoUsuario === '' ? '' : event.target.row.nombre;
        formSet({
            ...formValues,
            idEstadoUsuario: idEstadoUsuario,
            estadoUsuarioDesc: estadoUsuarioDesc
        });
    }

    const filteredData = useMemo(() => (
        state.list.filter(x => (
            (formValues.codigo.value === '' || x.codigo.toLowerCase().includes(formValues.codigo.toLowerCase()))
            && (formValues.nombreApellido.value === '' || x.nombreApellido.toLowerCase().includes(formValues.nombreApellido.toLowerCase()))
            && (formValues.email.value === '' || x.email.toLowerCase().includes(formValues.email.toLowerCase()))
            && (formValues.idEstadoUsuario === '' || x.idEstadoUsuario === parseInt(formValues.idEstadoUsuario))
        ))
    ), [filters, state.list])

    const [usuarios, setUsuarios] = useState([]);

    const [ getListLista ] = useLista({
        listas: ['EstadoUsuario'],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        },
        memo: { key: 'EstadoUsuario', timeout: 0 }
    });

    const [hasEditAccess, setHasEditAccess] = useState(false);
    useAccess({
        key: 'UsuariosView',
        onLoaded: (data, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
            else setHasEditAccess(data.some(x => x.codigo === 'usuario_edit'))
        }
    });

    const mount = () => {

        SearchUsuarios();

        const unmount = () => {}
        return unmount;
    }
    useEffect(mount, []);

    //definiciones
    const cellA = (props) => !hasEditAccess ? '' : (
        <div className='action'>
            <div onClick={ (event) => handleClickUsuarioAdd() } className="link">
                <span className="material-symbols-outlined" title="Nuevo">add</span>
            </div>
        </div>
    )

    const cellVMRB = (props) => (
        <div className='action'>
            <div onClick={ (event) => handleClickUsuarioView(props.value) } className="link">
                <span className="material-symbols-outlined" title="Ver">search</span>
            </div>
            {hasEditAccess && props.row.original.sistema &&
                <div title="Cuenta de sistema: no se puede modificar desde la aplicación">
                    <span className="material-symbols-outlined">lock</span>
                </div>
            }
            {hasEditAccess && !props.row.original.sistema && <>
                <div onClick={ (event) => handleClickUsuarioModify(props.value) } className="link">
                    <span className="material-symbols-outlined" title="Modificar">stylus</span>
                </div>
                <div onClick={ (event) => handleClickUsuarioRemove(props.value) } className="link">
                    <span className="material-symbols-outlined" title="Eliminar">delete</span>
                </div>
                <div onClick={ (event) => handleClickUsuarioBind(props.value) } className="link">
                    <span className="material-symbols-outlined" title="Asignar perfiles">link</span>
                </div>
            </>}
            <div onClick={() => handleClickUsuarioInfo(props.value)} className="link">
                <span className="material-symbols-outlined" title="Información adicional">info</span>
            </div>
        </div>
    )

    const cellEstado = (props) => getListLista('EstadoUsuario').find(x => x.id === props.value)?.nombre ?? ''

    const tableColumns = [
        { Header: 'Usuario', accessor: 'codigo', width: '20%' },
        { Header: 'Nombre y apellido', accessor: 'nombreApellido', width: '25%'},
        { Header: 'Email', accessor: 'email', disableSortBy: true, width: '25%' },
        { Header: 'Estado', Cell: cellEstado, accessor: 'idEstadoUsuario', width: '15%' },
        { Header: cellA, Cell: cellVMRB, accessor: 'id', width: '15%', ellipsis: false, disableGlobalFilter: true, disableSortBy: true},
    ];

    //handles
    const handleClickUsuarioAdd = () => {
        setState(prevState => {
            return {...prevState, showForm: true, modeFormEdit: true, rowId: 0};
        });
        setUsuarios(state.list.map(x => x.codigo.toLowerCase()));
    }
    const handleClickUsuarioView = (id) => {
        setState(prevState => {
            return {...prevState, showForm: true, modeFormEdit: false, rowId: parseInt(id)};
        });
    }
    const handleClickUsuarioModify = (id) => {
        setState(prevState => {
            return {...prevState, showForm: true, modeFormEdit: true, rowId: parseInt(id)};
        });
        setUsuarios(state.list.filter(x => x.id !== id).map(x => x.codigo.toLowerCase()));
    }
    const handleClickUsuarioRemove = (id) => {
        const usuario = state.list.find(x => x.id === id)
        showMessageModal({
            ...MESSAGE_MODALS.BORRAR,
            message: `¿Está seguro de borrar el usuario ${usuario.nombreApellido}?`,
            onConfirm: () => RemoveUsuario(id),
        })
    }
    const handleClickUsuarioBind = (id) => {
        const url = APPCONFIG.SITE.WEBAPP + 'usuario/perfiles/' + id;
        window.location.href = url;
    }

    const handleClickUsuarioInfo = (id) => {
        setDataTaggerId(id)
    }

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }

    //funciones
    function SearchUsuarios() {

        setState(prevState => {
            return {...prevState, loading: true, list: []};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((data) => {
                setState(prevState => {
                    return {...prevState, loading: false, list: data};
                });
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.USUARIO,
            null,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }

    function RemoveUsuario(id) {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((id) => {
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                SearchUsuarios();
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.USUARIO,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }

    function ApplyFilters() {
        UpdateFilters();
        SearchUsuarios();
    }

    function UpdateFilters() {
        let labels = [...filters.labels];
        labels.forEach((label) => {
            label.value = formValues[label.field];
        });
        setFilters(prevState => {
            return {...prevState, labels: labels};
        });
    }


    const dataTaggerRegistro = state.list.find(x => x.id === dataTaggerId)?.nombreApellido ?? ''

    return (
    <>

        <Loading visible={state.loading}></Loading>

        {state.showForm && 
            <UsuarioModal
                id={state.rowId}
                disabled={!state.modeFormEdit}
                data={{
                    listUsuarios: usuarios
                }}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, showForm: false, rowId: 0};
                    });
                }}
                onConfirm={(id) => {
                    setState(prevState => {
                        return {...prevState, showForm: false, rowId: 0};
                    });
                    SearchUsuarios();
                }}
            />
        }

        <SectionHeading titles={['Administración de usuarios/as']} />

        <section className='section-accordion'>

            <AdvancedSearch
                formValues={formValues}
                formSet={formSet}
                labels={filters.labels.filter(f => f.value !== f.valueIgnore)}
                onSearch={ApplyFilters}
            >

                <div className='row form-basic'>
                    <div className="col-12 col-md-3">
                        <label htmlFor="codigo" className="form-label">Código</label>
                        <InputWithEnterEvent
                            name="codigo"
                            type="text"
                            placeholder=""
                            className="form-control"
                            value={formValues.codigo}
                            onChange={ formHandle }
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-12 col-md-3">
                        <label htmlFor="nombre" className="form-label">Nombre</label>
                        <InputWithEnterEvent
                            name="nombreApellido"
                            type="text"
                            placeholder=""
                            className="form-control"
                            value={formValues.nombreApellido}
                            onChange={ formHandle }
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-12 col-md-3">
                        <label htmlFor="email" className="form-label">Email</label>
                        <InputWithEnterEvent
                            name="email"
                            type="text"
                            placeholder=""
                            className="form-control"
                            value={formValues.email}
                            onChange={ formHandle }
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-12 col-md-3">
                        <label htmlFor="idEstadoUsuario" className="form-label">Estado</label>
                        <Select
                            name="idEstadoUsuario"
                            className="form-control"
                            value={formValues.idEstadoUsuario}
                            placeholder="Todos"
                            onChange={ handleEstadoChange }
                            options={getListLista('EstadoUsuario').map((item) => ({ value: item.id, label: item.nombre }))}
                            emptyOption="Todos"
                            emptyValue=""
                        />
                    </div>
                </div>
            </AdvancedSearch>

            <div className="m-top-20">

                <TableCustom
                    className={'TableCustomBase'}
                    columns={tableColumns}
                    data={filteredData}
                />

            </div>

        </section>
        
        {dataTaggerId &&
            <DataTaggerModalApi
                title="Información adicional de usuario/a"
                registro={dataTaggerRegistro}
                tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_USUARIO_MODAL}
                entidad="Usuario"
                idEntidad={dataTaggerId}
                disabled={!hasEditAccess}
                onConfirm={() => setDataTaggerId(null)}
                onDismiss={() => setDataTaggerId(null)}
            />
        }
    </>
    )
}

export default UsuariosView;
